import { forwardRef, type CSSProperties, type LegacyRef } from 'react';
import * as styles from './AdminInput.module.css';

const CENTS_STEP_VALUE = '.01';

type AdminInputProps = {
  label: string;
  value: string;
  name: string;
  onChange: () => void;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
  required?: boolean;
  type?: string;
  characterLimit?: number;
  error?: string;
  datatestid?: string;
  dataRecurly?: string;
};

const AdminInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, AdminInputProps>(
  function ForwardedAdminInput(
    {
      label,
      value,
      name,
      onChange,
      placeholder,
      disabled,
      style,
      required,
      type,
      characterLimit,
      error,
      datatestid,
      //used for inputs in Payment.js that are using Recurly
      dataRecurly,
    },
    ref,
  ) {
    const resolveInputByType = () => {
      switch (type) {
        case 'text': {
          return (
            <input
              className={`${styles.input} ${error ? styles.error : ''}`}
              data-testid={datatestid || 'admin-input-text'}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type="text"
              required={required}
              disabled={disabled}
              maxLength={characterLimit}
              ref={ref as LegacyRef<HTMLInputElement>}
              data-recurly={dataRecurly}
            />
          );
        }
        case 'textArea': {
          return (
            <textarea
              className={`${styles.input} ${styles.textArea} ${error ? styles.error : ''}`}
              data-testid={datatestid || 'admin-input-textarea'}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              maxLength={characterLimit}
              ref={ref as LegacyRef<HTMLTextAreaElement>}
            />
          );
        }
        case 'price': {
          return (
            <div className={styles.priceInput}>
              <i className="far fa-dollar-sign" />
              <input
                data-testid={datatestid || 'admin-input-price'}
                className={`${styles.input} ${error ? styles.error : ''}`}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                type="number"
                step={CENTS_STEP_VALUE}
                min="0"
                pattern="^\d*(\.\d{0,2})?$"
                required={required}
                disabled={disabled}
                maxLength={characterLimit}
                ref={ref as LegacyRef<HTMLInputElement>}
              />
            </div>
          );
        }

        default: {
          return (
            <input
              data-testid={datatestid || 'admin-input-default'}
              className={`${styles.input} ${error ? styles.error : ''}`}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type={type}
              required={required}
              disabled={disabled}
              maxLength={characterLimit}
              ref={ref as LegacyRef<HTMLInputElement>}
            />
          );
        }
      }
    };

    return (
      <div className={styles.container} style={style}>
        <label htmlFor={name}>{label}</label>
        {resolveInputByType()}
        {/*color attribute error must be handled separately due to design of input*/}
        {error && label !== 'Color Attribute' && (
          <p className={styles.errorMessage} data-testid={`${label}-error`}>
            {error}
          </p>
        )}
      </div>
    );
  },
);

export default AdminInput;
